import { isVerificationStepKey, type VerificationStepKey } from '@aysar/contracts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/shared/hooks/useToast/useToast';
import { type ModalData } from '@/shared/store/reducers/modal/modal.types';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import { useResolveStepMutation } from '../../services/verification/verification.api';
import { verificationErrorCopy } from '../../utils/verification-error/verification-error.util';

/** Which of the two refusals this dialog is. */
export type StepVerdict = 'fail' | 'waive';

export interface StepVerdictTarget {
  caseId: string;
  stepKey: VerificationStepKey;
  verdict: StepVerdict;
  member: string | null;
}

export interface UseStepVerdictModalLogic {
  target: StepVerdictTarget | null;
  reason: string;
  setReason: (value: string) => void;
  canSubmit: boolean;
  isSending: boolean;
  error: string | null;
  submit: () => Promise<void>;
}

/**
 * Refusing or skipping one step.
 *
 * ## One hook and one dialog for two verdicts, deliberately
 *
 * Unlike approve/reject — which differ in what they *contain* — these two are
 * the same dialog: one step, one required reason, one mutation. What differs is
 * the copy and **who may open it**, and the permission belongs on the control
 * that opens it (`verification.manage` for waive, gated in `EvidenceStepCard`),
 * never on the dialog. Two components here would be two files to keep identical.
 *
 * ## The reason is required by the contract and by the dialog
 *
 * `WaiveStepSchema` types it `.trim().min(1).max(1000)` for both routes, and the
 * reason is why the audit row is worth anything: a waiver is a reviewer
 * overriding the deployment's own policy for one member. A blank one would leave
 * a record that somebody did something, and no record of why.
 */
export function useStepVerdictModalLogic(
  modalData: ModalData | undefined,
  onClose: () => void,
): UseStepVerdictModalLogic {
  const { t } = useTranslation('verification');
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [resolveStep, { isLoading: isSending }] = useResolveStepMutation();
  const target = readTarget(modalData);

  const submit = useCallback(async () => {
    if (target === null || reason.trim() === '') return;

    setError(null);

    try {
      await resolveStep({
        id: target.caseId,
        stepKey: target.stepKey,
        verdict: target.verdict,
        reason: reason.trim(),
      }).unwrap();

      toast.success(t(target.verdict === 'fail' ? 'failStep.success' : 'waiveStep.success'));
      onClose();
    } catch (caught) {
      const copy = verificationErrorCopy(toApiError(caught));
      setError(copy === null ? t('error.generic') : t(copy.i18nKey));
    }
  }, [onClose, reason, resolveStep, t, target, toast]);

  return {
    target,
    reason,
    setReason,
    canSubmit: target !== null && reason.trim() !== '' && !isSending,
    isSending,
    error,
    submit,
  };
}

/**
 * Narrows the modal payload.
 *
 * `ModalData` is `Record<string, unknown>` by design, so every field is checked
 * rather than cast — including the **verdict**, which decides which endpoint is
 * called and therefore which permission the api will check. A payload trusted
 * here would be a route chosen by whatever string happened to be in it.
 */
function readTarget(data: ModalData | undefined): StepVerdictTarget | null {
  if (data === undefined) return null;

  const caseId = data['caseId'];
  const stepKey = data['stepKey'];
  const verdict = data['verdict'];
  const member = data['member'];

  if (typeof caseId !== 'string' || caseId === '') return null;
  if (typeof stepKey !== 'string' || !isVerificationStepKey(stepKey)) return null;
  if (verdict !== 'fail' && verdict !== 'waive') return null;

  return {
    caseId,
    stepKey,
    verdict,
    member: typeof member === 'string' && member !== '' ? member : null,
  };
}
